import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import { useDesignElementManager } from "@hooks/use-design-element-manager";

import { Platform } from "../platforms/constants";

import { LEGACY_PLATFORMS, type LegacyPlatform } from "./legacy-platforms";

export const LegacyPlatformUTMImport = ({
  legacyPlatforms,
  setLegacyPlatforms,
  navigateFactory,
  isDisabled = false,
}: {
  legacyPlatforms: LegacyPlatform[];
  setLegacyPlatforms: (legacyPlatforms: LegacyPlatform[]) => void;
  navigateFactory: (platform: Platform) => (() => void) | undefined;
  isDisabled?: boolean;
}) => {
  const { designElementManager } = useDesignElementManager();
  const metaNav = navigateFactory(Platform.META);
  const tiktokNav = navigateFactory(Platform.TIKTOK);
  return (
    <div className="w-full flex-col justify-start items-start gap-6 inline-flex">
      <div className="self-stretch pt-10 flex-col justify-start items-start flex">
        <h4 className="font-semibold">
          If applicable, select legacy attribution tools
        </h4>
        <p className="m-0 p-0">
          Northbeam can automatically import historical ad tracking from some
          legacy attribution tools. All <i>new</i> ads launched must use
          Northbeam`s UTMs for tracking.
        </p>
      </div>
      <div className="flex flex-wrap gap-4">
        {LEGACY_PLATFORMS.data.map(({ label, value: name }) => {
          const isSelected = legacyPlatforms.includes(name);
          return (
            <GenericTooltip
              content="Uncheck the 'Complete' checkbox at the bottom of the screen to update"
              key={label}
              showTooltip={isDisabled}
              noInfoCircle
              placement="top"
            >
              <button
                className={clsx(
                  "w-[160px] h-[90px] items-center border rounded-lg flex flex-col justify-between pb-3",
                  {
                    "hover:bg-cosmic-5 hover:shadow-[inset_0px_0px_0px_1px_#7081FF]  hover:border-primary":
                      !isDisabled,
                    "bg-cosmic-5 border-primary shadow-[inset_0px_0px_0px_1px_#7081FF]":
                      isSelected,
                    "cursor-not-allowed": isDisabled,
                  },
                )}
                key={label}
                disabled={isDisabled}
                onClick={() => {
                  const platformIdx = legacyPlatforms.indexOf(name);
                  if (platformIdx < 0) {
                    setLegacyPlatforms([...legacyPlatforms, name]);
                  } else {
                    const platforms = [...legacyPlatforms];
                    platforms.splice(platformIdx, 1);
                    setLegacyPlatforms(platforms);
                  }
                }}
              >
                {isSelected && (
                  // check mark
                  <div className="w-full relative">
                    <div className="w-6 h-6 right-0 top-0 absolute">
                      <div className="w-full h-full flex bg-cosmic rounded-tr-lg justify-center items-center">
                        <i className="fa-regular fa-sharp fa-check text-white text-xs font-black" />
                      </div>
                    </div>
                  </div>
                )}
                <div className="self-stretch grow shrink basis-0 pt-2 flex-col justify-center items-center flex gap-2">
                  {designElementManager
                    .getDesignElement(label)
                    .getComponent({ size: "32" })}
                  <div className="text-center text-sm font-normal leading-none">
                    {label}
                  </div>
                </div>
              </button>
            </GenericTooltip>
          );
        })}
      </div>
      {legacyPlatforms.length > 0 && (
        <div className="w-full py-6 px-4 border rounded bg-pluto-2 space-y-4">
          {LEGACY_PLATFORMS.data
            .filter(({ value: name }) => legacyPlatforms.includes(name))
            .map(({ label, value: name }) => (
              <div key={name} className="flex gap-4 items-center">
                <i className="fa-sharp fa-regular fa-info-circle font-black text-pluto" />
                <span>
                  <b>{label} legacy UTM instructions have been added</b> to{" "}
                  {metaNav ? (
                    <a
                      className={clsx({})}
                      onClick={(e) => {
                        e.preventDefault();
                        metaNav();
                      }}
                    >
                      Meta Ads
                    </a>
                  ) : (
                    "Meta Ads"
                  )}
                  {name === "triple-whale" && (
                    <>
                      {" "}
                      and{" "}
                      {tiktokNav ? (
                        <a
                          className={clsx({})}
                          onClick={(e) => {
                            e.preventDefault();
                            tiktokNav();
                          }}
                        >
                          TikTok Ads
                        </a>
                      ) : (
                        "TikTok Ads"
                      )}
                    </>
                  )}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};
